import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, formatCOP, imageUrl } from '../services/api';
import usePickList, { itemPickKey } from '../hooks/usePickList';
import { enqueueBodegaMutation, isNetworkError } from '../utils/offlineBodegaQueue';
import { entregaParts, pedidoDatosFicha } from '../utils/entregaAddress';
import ImageZoomLightbox from '../components/ImageZoomLightbox';

const ESTADO_LABEL = {
  confirmado: 'Por preparar',
  en_preparacion: 'En preparación',
  entregado: 'Completado',
};

const SEP_KEY = 'oh_bodega_separador';

function fmtFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function loadSaved(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function parseMissingOnPedido(pedido) {
  const raw = pedido?.missing_keys;
  if (Array.isArray(raw)) return new Set(raw.map(String));
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      return new Set();
    }
  }
  return new Set();
}

export default function BodegaReview({ id, pin, usuario, onUpdated, onBack }) {
  const savedSep = loadSaved(SEP_KEY);
  const [pedido, setPedido] = useState(null);
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [separadores, setSeparadores] = useState([]);
  // Tablet compartida: el PIN abre la app; quién separa se elige de la lista (no el usuario PIN fijo).
  const [separadorId, setSeparadorId] = useState(() => savedSep?.id || '');
  const [separadorNombre, setSeparadorNombre] = useState(() => savedSep?.nombre || '');
  const [notasBodega, setNotasBodega] = useState('');
  const [savingNotas, setSavingNotas] = useState(false);
  const notasTimer = useRef(null);
  const notasDirty = useRef(false);
  const pickingRef = useRef(false);

  const items = pedido?.items || [];
  // Tablet compartida: quien inicia sesión puede separar y completar.
  const yoSeparando = pedido?.estado === 'en_preparacion';
  pickingRef.current = yoSeparando;
  const otroSeparando = false;
  const canastaWho = pedido?.preparado_por_nombre || separadorNombre || null;

  const canEditCanasta = Boolean(yoSeparando);
  const canEditNotas =
    pedido?.estado === 'confirmado' ||
    (pedido?.estado === 'en_preparacion' && (yoSeparando || !pedido?.preparado_por_nombre));

  const {
    picked,
    missing,
    toggle,
    markMissing,
    done,
    donePicked,
    doneMissing,
    total,
    allDone,
    progress,
    saveError,
    savedOffline,
    loaded: canastaLoaded,
  } = usePickList(id, items, pin, canEditCanasta ? canastaWho : null, {
    readOnly: !canEditCanasta,
  });

  // Mantener notas con líneas "NO HAY: …" al marcar faltantes.
  useEffect(() => {
    if (!canastaLoaded || !canEditNotas || !pedido || !canEditCanasta) return;
    const other = String(notasBodega || '')
      .split('\n')
      .filter((line) => !/^NO HAY:/i.test(line.trim()))
      .join('\n')
      .trim();
    const faltanLines = items
      .map((it, idx) => ({ it, key: itemPickKey(it, idx) }))
      .filter(({ key }) => missing.has(key))
      .map(
        ({ it }) =>
          `NO HAY: ${it.codigo} ${it.nombre || ''} (×${Number(it.cantidad) || 0})`.trim()
      );
    const next = [other, ...faltanLines].filter(Boolean).join('\n');
    if (next === String(notasBodega || '').trim()) return;
    notasDirty.current = true;
    setNotasBodega(next);
  }, [missing, items, canEditNotas, canEditCanasta, pedido, canastaLoaded, notasBodega]);


  const reload = useCallback(async () => {
    const res = await api.getPedido(id);
    setPedido(res.data);
    if (!notasDirty.current) {
      setNotasBodega(res.data?.notas_bodega || '');
    }
    return res.data;
  }, [id, pin]);

  useEffect(() => {
    notasDirty.current = false;
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getSeparadores();
        if (!cancelled) setSeparadores(res.data || []);
      } catch {
        if (!cancelled) setSeparadores([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        await reload();
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, pin, reload]);

  useEffect(() => {
    if (!id || !pin) return undefined;
    const t = setInterval(() => {
      if (pickingRef.current) return;
      reload().catch(() => {});
    }, 45000);
    const onVis = () => {
      if (document.visibilityState === 'visible' && !pickingRef.current) reload().catch(() => {});
    };
    const onInv = (ev) => {
      const domains = ev.detail?.domains || [];
      if (!domains.includes('order')) return;
      // Cada marca emite canasta_actualizada: recargar aquí pisaba las marcas.
      if (pickingRef.current) return;
      reload().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('oh:invalidate', onInv);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('oh:invalidate', onInv);
    };
  }, [id, pin, reload]);

  useEffect(() => {
    if (!canEditNotas || !pedido || !pin) return undefined;
    clearTimeout(notasTimer.current);
    if (!notasDirty.current) return undefined;
    notasTimer.current = setTimeout(async () => {
      setSavingNotas(true);
      try {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          enqueueBodegaMutation({
            type: 'notas',
            pedidoId: id,
            notas: notasBodega,
            separadorNombre: yoSeparando ? canastaWho : null,
          });
          notasDirty.current = false;
          setError('');
          return;
        }
        const res = await api.saveNotasBodega(
          id,
          notasBodega,
          yoSeparando ? canastaWho : null
        );
        setPedido(res.data);
        notasDirty.current = false;
        setError('');
      } catch (err) {
        if (isNetworkError(err)) {
          enqueueBodegaMutation({
            type: 'notas',
            pedidoId: id,
            notas: notasBodega,
            separadorNombre: yoSeparando ? canastaWho : null,
          });
          notasDirty.current = false;
          setError('Sin internet — notas guardadas en la tablet. Se enviarán al volver la señal.');
        } else {
          setError(err.message);
        }
      } finally {
        setSavingNotas(false);
      }
    }, 600);
    return () => clearTimeout(notasTimer.current);
  }, [notasBodega, canEditNotas, pedido, pin, id, yoSeparando, canastaWho]);

  function onPickSeparador(e) {
    const val = e.target.value;
    const found = separadores.find((s) => String(s.id) === String(val));
    if (found) {
      setSeparadorId(found.id);
      setSeparadorNombre(found.nombre);
      sessionStorage.setItem(SEP_KEY, JSON.stringify({ id: found.id, nombre: found.nombre }));
    } else {
      setSeparadorId('');
      setSeparadorNombre('');
    }
  }

  function onNotasChange(e) {
    notasDirty.current = true;
    setNotasBodega(e.target.value);
  }

  async function cambiarEstado(estado) {
    if (estado === 'en_preparacion' && !separadorNombre) {
      setError('Elige quién va a separar el pedido');
      return;
    }
    if (estado === 'entregado' && !allDone && total > 0) {
      setError(`Faltan ${total - done} producto(s) por marcar (canasta o “no hay”)`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (notasDirty.current && canEditNotas) {
        await api.saveNotasBodega(id, notasBodega, yoSeparando ? canastaWho : null);
        notasDirty.current = false;
      }
      if (estado === 'entregado' && yoSeparando) {
        await api.saveCanasta(id, [...picked], canastaWho, undefined, [...missing]);
      }
      const res = await api.actualizarEstado(id, estado, {
        separadorId,
        separadorNombre,
      });
      setPedido(res.data);
      onUpdated?.();
    } catch (err) {
      const detail =
        err?.code === 'CANASTA_INCOMPLETA'
          ? err.message
          : err.message || 'No se pudo actualizar el pedido';
      setError(detail);
      if (err.status === 409 || err.status === 400 || err.status === 403) {
        try {
          await reload();
        } catch {
          /* ignore */
        }
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="review">
        <p className="muted center">Cargando pedido...</p>
      </div>
    );
  }
  if (error && !pedido) {
    return (
      <div className="review">
        <p className="error center">{error}</p>
        <button type="button" className="btn-back" onClick={onBack}>
          ← Volver
        </button>
      </div>
    );
  }
  if (!pedido) {
    return (
      <div className="review">
        <p className="empty center">Pedido no encontrado</p>
      </div>
    );
  }

  const totalUnidades = items.reduce((s, it) => s + (Number(it.cantidad) || 0), 0);
  const canPick = pedido.estado === 'confirmado' || pedido.estado === 'en_preparacion';
  const separador = pedido.preparado_por_nombre || separadorNombre;
  // Firma = elegir persona del equipo (no hay checkbox aparte).
  const firmaStartBlocked = pedido.estado === 'confirmado' && !separadorNombre;
  const showCompletar =
    pedido.estado === 'en_preparacion' &&
    (yoSeparando || otroSeparando || !!pedido.preparado_por_nombre);
  const entrega = entregaParts(pedido);
  const ficha = pedidoDatosFicha(pedido);

  return (
    <div className="review">
      <header className="review-head">
        <button type="button" className="btn-back" onClick={onBack}>
          ← Lista
        </button>
        <div className="review-head-text">
          <h2>{pedido.numero}</h2>
          <p className="muted">
            {ESTADO_LABEL[pedido.estado] || pedido.estado}
            {pedido.tipo_documento === 'iva' ? ' · Factura' : ' · Remisión'}
          </p>
        </div>
      </header>

      <div className="review-ficha">
        <section>
          <h3>Cliente / sucursal</h3>
          <p>
            <span>Nombre</span>
            <strong>
              {ficha.nombre}
              {ficha.esSede ? <em className="sede-pill">Sucursal</em> : null}
            </strong>
          </p>
          <p>
            <span>Cód.</span>
            <strong>{ficha.codigo}</strong>
          </p>
          <p>
            <span>Tel.</span>
            <strong>{ficha.telefono}</strong>
          </p>
          <p>
            <span>Dirección</span>
            <strong>{ficha.direccion}</strong>
          </p>
          {ficha.ciudadZona ? (
            <p>
              <span>Ciudad</span>
              <strong>{ficha.ciudadZona}</strong>
            </p>
          ) : null}
          {ficha.sedeEtiqueta ? (
            <p>
              <span>Sede</span>
              <strong>{ficha.sedeEtiqueta}</strong>
            </p>
          ) : null}
        </section>
        <section>
          <h3>Condiciones</h3>
          <p>
            <span>Vendedor</span>
            <strong>{ficha.vendedor}</strong>
          </p>
          <p>
            <span>Pago</span>
            <strong>{ficha.pago}</strong>
          </p>
          <p>
            <span>Horario recibo</span>
            <strong>{ficha.horario}</strong>
          </p>
          {ficha.transportadora ? (
            <p>
              <span>Transportadora</span>
              <strong>{ficha.transportadora}</strong>
            </p>
          ) : null}
        </section>
        <section className="review-ficha-obs">
          <h3>Observaciones</h3>
          <p>{ficha.observaciones || 'Sin observaciones'}</p>
        </section>
      </div>

      {error ? <p className="error banner">{error}</p> : null}

      {otroSeparando ? (
        <div className="claim-banner claim-otro">
          <strong>Lo está separando {pedido.preparado_por_nombre}</strong>
          <p className="muted">Puedes revisar y firmar al completar, pero no editar la canasta.</p>
        </div>
      ) : null}

      {yoSeparando ? (
        <div className="claim-banner claim-mio">
          <strong>Tú estás separando este pedido ({separadorNombre})</strong>
        </div>
      ) : null}

      {pedido.estado === 'entregado' ? (
        <div className="completado-banner">
          <span className="completado-icon">✓</span>
          <div>
            <strong>Pedido COMPLETADO</strong>
            <p>
              Separado por {separador}
              {pedido.preparado_at ? ` · ${fmtFecha(pedido.preparado_at)}` : ''}
            </p>
          </div>
        </div>
      ) : null}

      <div className="pick-summary">
        <span>
          <strong>{items.length}</strong> productos
        </span>
        <span>
          <strong>{totalUnidades}</strong> unidades
        </span>
        <span>{formatCOP(pedido.subtotal)}</span>
      </div>

      {canPick && total > 0 ? (
        <div className="canasta-progress">
          <div className="canasta-progress-head">
            <strong>Canasta de separación</strong>
            <span>
              {done} de {total} listos
              {doneMissing > 0 ? ` · ${doneMissing} sin producto` : ''}
            </span>
          </div>
          <div className="canasta-progress-bar">
            <div className="canasta-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          {allDone ? (
            <p className="canasta-done-msg">
              ✓ Todo marcado
              {doneMissing > 0 ? ` (${donePicked} en canasta · ${doneMissing} no hay)` : ' — baja y pulsa COMPLETADO'}
            </p>
          ) : (
            <p className="canasta-hint">
              Marca cada producto en canasta, o “No hay” si falta en bodega
            </p>
          )}
          {saveError ? <p className="error banner">{saveError}</p> : null}
          {savedOffline && !saveError ? (
            <p className="muted banner">Canasta en tablet — se sincroniza al volver internet.</p>
          ) : null}
        </div>
      ) : null}

      <div className="pick-list">
        <h3>Productos a separar</h3>
        {!items.length ? (
          <p className="muted">Este pedido no tiene productos.</p>
        ) : (
          <ul>
            {items.map((it, idx) => {
              const key = itemPickKey(it, idx);
              const isMissing =
                pedido.estado === 'entregado'
                  ? parseMissingOnPedido(pedido).has(key)
                  : missing.has(key);
              const inCanasta =
                !isMissing && (pedido.estado === 'entregado' || picked.has(key));
              return (
                <li
                  key={key}
                  className={`pick-item ${inCanasta ? 'pick-item-done' : ''} ${
                    isMissing ? 'pick-item-missing' : ''
                  }`}
                >
                  <div className="pick-actions">
                    {canEditCanasta ? (
                      <>
                        <button
                          type="button"
                          className={`pick-check ${inCanasta ? 'checked' : ''}`}
                          onClick={() => toggle(key)}
                          aria-pressed={inCanasta}
                        >
                          <span className="pick-check-icon">{inCanasta ? '✓' : '○'}</span>
                          <span className="pick-check-label">
                            {inCanasta ? 'En canasta' : 'Marcar'}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`pick-missing-btn ${isMissing ? 'active' : ''}`}
                          onClick={() => markMissing(key)}
                          aria-pressed={isMissing}
                        >
                          {isMissing ? 'Sin producto' : 'No hay'}
                        </button>
                      </>
                    ) : (
                      <span
                        className={`pick-check ${inCanasta || isMissing ? 'checked' : ''} ${
                          isMissing ? 'missing' : ''
                        } pick-check-readonly`}
                      >
                        <span className="pick-check-icon">
                          {isMissing ? '!' : inCanasta ? '✓' : '○'}
                        </span>
                        {isMissing ? (
                          <span className="pick-check-label">Sin producto</span>
                        ) : null}
                      </span>
                    )}
                  </div>

                  <div className="pick-item-photo">
                    {it.imagen ? (
                      <button
                        type="button"
                        className="pick-item-photo-btn"
                        onPointerUp={(e) => {
                          // Android WebView a veces come el click; pointerUp es más fiable
                          if (e.button != null && e.button !== 0) return;
                          e.preventDefault();
                          e.stopPropagation();
                          const src = imageUrl(it.imagen);
                          if (!src) return;
                          setZoomPhoto({
                            src,
                            alt: it.nombre || it.codigo || 'Producto',
                          });
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        aria-label={`Abrir foto ${it.codigo || ''}`}
                        title="Toca para ver la foto grande"
                      >
                        <img src={imageUrl(it.imagen)} alt="" draggable={false} />
                        <span className="pick-photo-zoom-badge">🔍 Ver foto</span>
                      </button>
                    ) : (
                      <div className="pick-item-img-empty" aria-hidden="true" />
                    )}
                  </div>

                  <div className="pick-item-info">
                    <strong>{it.codigo}</strong>
                    <span>{it.nombre}</span>
                    {isMissing ? <em className="pick-missing-tag">No hay en bodega</em> : null}
                    {it.observaciones ? (
                      <em className="pick-item-obs">Obs: {it.observaciones}</em>
                    ) : null}
                  </div>

                  <div className="pick-qty">× {it.cantidad}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="bodega-notas-card">
        {pedido.notas?.trim() ? (
          <div className="bodega-pedido-notas" style={{ marginBottom: 12 }}>
            <h4>Observaciones del vendedor</h4>
            <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{pedido.notas}</p>
          </div>
        ) : (
          <div className="bodega-pedido-notas" style={{ marginBottom: 12 }}>
            <h4>Observaciones del vendedor</h4>
            <p className="muted" style={{ margin: '6px 0 0' }}>Sin observaciones</p>
          </div>
        )}
        <div className="bodega-notas-head">
          <h4>Notas de bodega</h4>
          {savingNotas ? (
            <span className="muted">
              {typeof navigator !== 'undefined' && !navigator.onLine
                ? 'Guardando en tablet…'
                : 'Guardando…'}
            </span>
          ) : null}
        </div>
        {canEditNotas ? (
          <textarea
            className="bodega-notas-input"
            rows={3}
            maxLength={2000}
            placeholder="Ej: faltó 1 unidad, se cambió por otro color, cliente pidió urgente…"
            value={notasBodega}
            onChange={onNotasChange}
          />
        ) : (
          <p className="bodega-notas-readonly">
            {pedido.notas_bodega?.trim() ? pedido.notas_bodega : 'Sin notas de bodega'}
          </p>
        )}
      </div>

      <div className="sep-card sep-card-inline">
        <h4>Registro</h4>
        {entrega.direccion || entrega.ciudadZona ? (
          <div className="sep-entrega">
            <span>Dirección de entrega</span>
            {entrega.direccion ? <strong>{entrega.direccion}</strong> : null}
            {entrega.ciudadZona ? <em>{entrega.ciudadZona}</em> : null}
          </div>
        ) : null}
        {pedido.estado === 'confirmado' && !pedido.preparado_por_nombre ? (
          <label className="separador-pick">
            <span>Quién separa</span>
            <select value={separadorId || ''} onChange={onPickSeparador}>
              <option value="">— Elige del equipo —</option>
              {separadores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </label>
        ) : pedido.preparado_por_nombre ? (
          <p className="sep-row">
            <span>{pedido.estado === 'entregado' ? 'Separado por' : 'Separando'}</span>
            <strong>{pedido.preparado_por_nombre}</strong>
          </p>
        ) : (
          <p className="sep-row muted">Aún sin reclamar</p>
        )}
        {pedido.notas_bodega?.trim() ? (
          <p className="sep-notas">
            <span>Notas</span>
            <strong>{pedido.notas_bodega}</strong>
          </p>
        ) : null}
      </div>

      {pedido.estado !== 'entregado' ? (
        <div className="review-sticky-bar">
          {pedido.estado === 'confirmado' && !otroSeparando ? (
            <button
              type="button"
              className="btn-primary btn-block btn-action-main"
              disabled={saving || firmaStartBlocked}
              onClick={() => cambiarEstado('en_preparacion')}
            >
              {saving ? 'Guardando...' : '▶ Empezar a separar'}
            </button>
          ) : null}
          {firmaStartBlocked && pedido.estado === 'confirmado' ? (
            <p className="sticky-hint">Elige quién separa antes de empezar</p>
          ) : null}

          {showCompletar ? (
            <>
              <p className="sticky-hint">Marca los productos (canasta o “No hay”) y completa</p>
              <button
                type="button"
                className={`btn-primary btn-block btn-action-main btn-completado ${allDone ? 'pulse' : ''}`}
                disabled={saving || !allDone}
                onClick={() => cambiarEstado('entregado')}
              >
                {saving ? 'Guardando...' : '✓ Marcar pedido completado'}
              </button>
              {!allDone ? (
                <p className="sticky-hint">Faltan productos por marcar</p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
      {zoomPhoto?.src ? (
        <ImageZoomLightbox
          src={zoomPhoto.src}
          alt={zoomPhoto.alt}
          onClose={() => setZoomPhoto(null)}
        />
      ) : null}
    </div>
  );
}
